/**
 * 1.8.2 — the events calendar on /events (D-1.8.2-2): the month as a grid and the keyboard's move by day.
 *
 * The grid is weeks as rows, Sunday first, seven places a week; a place outside the month is empty (null). Each event
 * is placed on the day it lands. A Repeat is not a rule drawn here: New event makes every event of a repeat as its own
 * event (1.7.7, D-1.7.7-2 — "each event stands alone"), so a repeat is drawn on every day it lands simply because each
 * of its events is placed on its own day — the last Thursday of October on the 29th, of November on the 26th — and a
 * month a repeat does not reach shows nothing of it.
 *
 * Pure, civil day keys only (`yyyyMMdd`); nothing here converts an instant.
 */
import { addDaysToKey, dayOfWeek, isDateKey } from './dates';
import { addMonths, daysInMonth, monthOf } from './event-calendar';

/** What the grid needs of an event. */
export interface GridEvent {
  eventId: string;
  dayKey: string;
  minutes: number | null;
  name: string;
}

export interface GridCell<T extends GridEvent> {
  /** The day, or null for a place before the 1st or after the last day. */
  dayKey: string | null;
  /** The day's events: in the order of the clock (an event with no time first), then by name. */
  events: T[];
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Every day of the month, first to last. */
export function gridDays(month: string): string[] {
  return Array.from({ length: daysInMonth(month) }, (_, i) => `${month}${pad(i + 1)}`);
}

/** The month's grid: weeks as rows, Sunday first, each event on its own day (the same event once). */
export function monthGrid<T extends GridEvent>(month: string, events: readonly T[]): GridCell<T>[][] {
  const byDay = new Map<string, T[]>();
  const seen = new Set<string>();
  for (const e of events) {
    if (!e.dayKey.startsWith(month) || seen.has(e.eventId)) continue;
    seen.add(e.eventId);
    byDay.set(e.dayKey, [...(byDay.get(e.dayKey) ?? []), e]);
  }
  for (const list of byDay.values()) list.sort((a, b) => (a.minutes ?? -1) - (b.minutes ?? -1) || a.name.localeCompare(b.name));
  const cells: GridCell<T>[] = [...Array.from({ length: dayOfWeek(`${month}01`) }, () => ({ dayKey: null, events: [] as T[] })), ...gridDays(month).map((dayKey) => ({ dayKey, events: byDay.get(dayKey) ?? [] }))];
  while (cells.length % 7) cells.push({ dayKey: null, events: [] });
  const weeks: GridCell<T>[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** The keys the grid answers. */
export const GRID_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'] as const;

/** The same day of another month, or that month's last day where it has no such day (Mar 31 → Feb 28). */
function sameDayIn(dayKey: string, delta: number): string {
  const m = addMonths(monthOf(dayKey), delta);
  const d = Math.min(Number(dayKey.slice(6, 8)), daysInMonth(m));
  return `${m}${pad(d)}`;
}

/**
 * Where a key moves the focus from a day: ← → a day, ↑ ↓ a week, Home / End the week's Sunday / Saturday, Page Up /
 * Page Down the same day a month before / after. Across the month's edge it goes on into the next or last month. Any
 * other key: null (the grid lets it be).
 */
export function moveDay(dayKey: string, key: string): string | null {
  if (!isDateKey(dayKey)) return null;
  switch (key) {
    case 'ArrowLeft':
      return addDaysToKey(dayKey, -1);
    case 'ArrowRight':
      return addDaysToKey(dayKey, 1);
    case 'ArrowUp':
      return addDaysToKey(dayKey, -7);
    case 'ArrowDown':
      return addDaysToKey(dayKey, 7);
    case 'Home':
      return addDaysToKey(dayKey, -dayOfWeek(dayKey));
    case 'End':
      return addDaysToKey(dayKey, 6 - dayOfWeek(dayKey));
    case 'PageUp':
      return sameDayIn(dayKey, -1);
    case 'PageDown':
      return sameDayIn(dayKey, 1);
    default:
      return null;
  }
}
